import type { User as AuthUser } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { User, Edit, ChevronDown, ChevronUp, MessageSquare, StickyNote, CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MemberSetting } from "@/types/purchase";
import type { CustomerTemplate } from "@/components/CustomerTemplateManager";

export interface LgMessageHeaderToolbarProps {
  titleStyle: string;
  user: AuthUser | null;
  memberSetting: MemberSetting;
  activeCustomer: CustomerTemplate | null | undefined;
  showShippingSettingsInHome: boolean;
  setShowShippingSettingsInHome: (value: boolean) => void;
  showTemplateEditorInHome: boolean;
  setShowTemplateEditorInHome: (value: boolean) => void;
  openMoveOrderDateDialog: () => void;
  // cross-tab 메모 버튼 — memoInput/isEditingMemo state · inlineMemoRef · focusInlineMemoEnd 정의는
  // 전부 Home 소유. 여긴 setter + focusInlineMemoEnd만 prop으로 받아 호출한다.
  setMemoInput: (value: string) => void;
  setIsEditingMemo: (value: boolean) => void;
  focusInlineMemoEnd: () => void;
}

/**
 * lg(1024-1279px) message-tab 전용 헤더 툴바 — "메시지 정보" 제목 + 4버튼(배송/결제 설정 토글 ·
 * 템플릿 편집 토글 · 날짜 변경 · 메모) + 활성 고객 안내 배너.
 * xl/mobile의 MessageHeaderToolbar(2버튼, `{activeCustomer}` 배너)와 의도적으로 분리한다 — lg는
 * 날짜 변경 · cross-tab 메모버튼이 더 있고 배너 가드가 `{user && activeCustomer}`라 별도로 소유.
 *
 * ★cross-tab focus hazard: 메모 버튼은 message 탭에 있으나 포커스 대상 Textarea(inlineMemoRef)는
 * product 탭에 있다. onClick의 setMemoInput→setIsEditingMemo(true)→focusInlineMemoEnd() 순서가
 * caret 점프를 만든다(순서 보존 필수). memoInput/isEditingMemo state · inlineMemoRef ·
 * focusInlineMemoEnd 정의 · re-focus useLayoutEffect는 전부 Home 소유 — 이 컴포넌트는 setter +
 * focusInlineMemoEnd만 prop으로 받고 그 값 자체는 읽지 않는다(버튼 상태는 activeCustomer.memo 기준).
 */
export function LgMessageHeaderToolbar({
  titleStyle,
  user,
  memberSetting,
  activeCustomer,
  showShippingSettingsInHome,
  setShowShippingSettingsInHome,
  showTemplateEditorInHome,
  setShowTemplateEditorInHome,
  openMoveOrderDateDialog,
  setMemoInput,
  setIsEditingMemo,
  focusInlineMemoEnd,
}: LgMessageHeaderToolbarProps) {
  return (
    <>
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div className={`${titleStyle} flex items-center`}>
          <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
          메시지 정보
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showShippingSettingsInHome ? "secondary" : "ghost"}
            size="sm"
            className={cn(
              "text-sm font-medium relative px-3 py-1.5 rounded-md",
              showShippingSettingsInHome
                ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
            )}
            onClick={() => {
              setShowShippingSettingsInHome(!showShippingSettingsInHome);
              if (!showShippingSettingsInHome) setShowTemplateEditorInHome(false);
            }}
            data-tour="settings-toggle"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-1.5"><path d="M17.25 6.75L22.5 12l-5.25 5.25"></path><path d="M2.25 12h19.5"></path></svg>
            배송/결제 설정
            {showShippingSettingsInHome ? <ChevronUp className="w-3.5 h-3.5 ml-1.5" /> : <ChevronDown className="w-3.5 h-3.5 ml-1.5" />}
            {user && !showShippingSettingsInHome && !memberSetting.depositAccount && (
              <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500 border-white border"></span>
              </span>
            )}
          </Button>
          <Button
            variant={showTemplateEditorInHome ? "secondary" : "ghost"}
            size="sm"
            className={cn(
              "text-sm font-medium px-3 py-1.5 rounded-md",
              showTemplateEditorInHome
                ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
            )}
            onClick={() => {
              setShowTemplateEditorInHome(!showTemplateEditorInHome);
              if (!showTemplateEditorInHome) setShowShippingSettingsInHome(false);
            }}
          >
            <Edit className="w-4 h-4 mr-1.5" />
            템플릿 편집
            {showTemplateEditorInHome ? <ChevronUp className="w-3.5 h-3.5 ml-1.5" /> : <ChevronDown className="w-3.5 h-3.5 ml-1.5" />}
          </Button>
          {user && activeCustomer && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="text-sm px-3 py-1.5 rounded-md"
                onClick={() => openMoveOrderDateDialog()}
              >
                <CalendarDays className="w-4 h-4 mr-1.5" />
                날짜 변경
              </Button>
            </>
          )}
          {user && activeCustomer && (
            <Button
              variant={activeCustomer?.memo ? "secondary" : "ghost"}
              size="sm"
              className={cn(
                "text-sm font-medium px-3 py-1.5 rounded-md",
                activeCustomer?.memo
                  ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
              )}
              onClick={() => {
                setMemoInput(activeCustomer?.memo || '');
                setIsEditingMemo(true);
                focusInlineMemoEnd();
              }}
            >
              <StickyNote className="w-4 h-4 mr-1.5" />
              메모
            </Button>
          )}
        </div>
      </div>
      {user && activeCustomer && (
        <div className="mt-2.5">
          <div className="flex items-center text-xs text-indigo-700 bg-indigo-50 px-2.5 py-1.5 rounded-md">
            <User className="w-3.5 h-3.5 mr-1.5 shrink-0" />
            <span className="font-medium">
              현재 <strong className="text-indigo-800">{activeCustomer.nickname}</strong> 님의 상품 목록으로 메시지가 생성됩니다.
            </span>
          </div>
        </div>
      )}
    </>
  );
}
